import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LineChart, Zap, Shield, TrendingUp, Lock, Activity, Globe, ChevronRight } from "lucide-react";

const TICKER_ITEMS = [
  { sym: "XAU/USD", label: "Gold" },
  { sym: "BTC/USD", label: "Bitcoin" },
  { sym: "US30",    label: "Dow Jones" },
  { sym: "US100",   label: "Nasdaq" },
  { sym: "EUR/USD", label: "Euro" },
  { sym: "US500",   label: "S&P 500" },
  { sym: "ETH/USD", label: "Ethereum" },
  { sym: "GBP/USD", label: "Sterling" },
  { sym: "USD/JPY", label: "Yen" },
];

export default function Landing() {
  // Counter animation for the headline stat — pure feel-good.
  const [count, setCount] = useState(0);
  useEffect(() => {
    const target = 1247;
    const start = Date.now();
    const duration = 1400;
    const id = setInterval(() => {
      const t = Math.min((Date.now() - start) / duration, 1);
      setCount(Math.floor(target * (1 - Math.pow(1 - t, 3))));
      if (t >= 1) clearInterval(id);
    }, 30);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="min-h-screen bg-[#09090B] text-zinc-100 overflow-x-hidden">
      {/* Top bar */}
      <header className="sticky top-0 z-30 backdrop-blur bg-[#09090B]/85 border-b border-white/5">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <LineChart className="w-5 h-5 text-teal-400" strokeWidth={1.5} />
            <div className="font-mono text-sm tracking-widest uppercase" data-testid="brand-mark">BonBot</div>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <Link to="/login" data-testid="header-login-link"
                  className="text-xs sm:text-sm text-zinc-400 hover:text-zinc-100 font-mono uppercase tracking-widest transition-colors">
              Sign in
            </Link>
            <Link to="/register" data-testid="header-register-link"
                  className="bg-teal-400/10 text-teal-400 border border-teal-400/30 hover:bg-teal-400/20 px-3 sm:px-4 py-2 text-xs sm:text-sm font-mono uppercase tracking-widest rounded-sm transition-all">
              Get Access
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        {/* Background mesh */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-teal-400/[0.07] rounded-full blur-[120px]" />
          <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-cyan-400/[0.05] rounded-full blur-[120px]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_30%,#09090B_85%)]" />
        </div>

        <div className="max-w-6xl mx-auto px-5 pt-16 sm:pt-24 lg:pt-32 pb-12 relative">
          {/* Status pill */}
          <div className="inline-flex items-center gap-2 border border-teal-400/30 bg-teal-400/[0.05] px-3 py-1.5 rounded-full mb-7" data-testid="live-status-pill">
            <span className="relative flex w-2 h-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-teal-400" />
            </span>
            <span className="text-[10px] font-mono uppercase tracking-widest text-teal-400">Engine Live · 99.97% Uptime</span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-medium tracking-tight leading-[1.04] max-w-4xl">
            Your broker.
            <br />
            Our engine.
            <br />
            <span className="bg-gradient-to-r from-teal-300 via-teal-400 to-cyan-400 bg-clip-text text-transparent">
              Trades that don't sleep.
            </span>
          </h1>

          <p className="text-zinc-400 text-base sm:text-lg lg:text-xl mt-7 max-w-2xl leading-relaxed">
            Connect your MT5 broker once. Pick how you want to trade. Walk away.
            We handle entries, exits, and risk while you live your life.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-3">
            <Link to="/register" data-testid="hero-cta-register"
                  className="group bg-teal-400 text-[#09090B] hover:bg-teal-300 px-6 py-3.5 text-sm font-mono uppercase tracking-widest rounded-sm font-medium transition-all flex items-center gap-2">
              Start free
              <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" strokeWidth={2} />
            </Link>
            <Link to="/login" data-testid="hero-cta-login"
                  className="border border-white/20 text-zinc-200 hover:bg-white/5 px-6 py-3.5 text-sm font-mono uppercase tracking-widest rounded-sm transition-colors">
              I have an account
            </Link>
          </div>

          {/* Headline counter */}
          <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/5 border border-white/10 rounded-sm overflow-hidden max-w-3xl">
            <Stat value={count.toLocaleString()} label="Trades executed" />
            <Stat value="99.97%" label="Uptime" tone="teal" />
            <Stat value="<2s" label="Signal-to-fill" />
            <Stat value="24/7" label="Cloud hosted" tone="teal" />
          </div>
        </div>

        {/* Asset ticker strip */}
        <div className="relative border-y border-white/10 bg-[#0b0b0d] overflow-hidden">
          <div className="flex gap-12 animate-[ticker_40s_linear_infinite] py-5 whitespace-nowrap" style={{ width: "max-content" }}>
            {[...TICKER_ITEMS, ...TICKER_ITEMS, ...TICKER_ITEMS].map((it, i) => (
              <div key={i} className="flex items-center gap-3 px-2">
                <span className="text-teal-400 font-mono text-sm tracking-widest">{it.sym}</span>
                <span className="text-zinc-600 text-xs">{it.label}</span>
              </div>
            ))}
          </div>
          <style>{`
            @keyframes ticker {
              from { transform: translateX(0); }
              to   { transform: translateX(-33.333%); }
            }
          `}</style>
        </div>
      </section>

      {/* Markets section */}
      <section className="border-b border-white/5 bg-[#0a0a0c]">
        <div className="max-w-6xl mx-auto px-5 py-20">
          <div className="text-[10px] uppercase tracking-[0.25em] text-teal-400 mb-4 font-mono">
            // Markets
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight leading-tight max-w-3xl">
            Forex. Crypto.<br />
            Indices. <span className="text-teal-400">One bot.</span>
          </h2>
          <p className="text-zinc-400 mt-5 max-w-2xl leading-relaxed">
            Trade what moves you — major / minor pairs, gold &amp; silver, BTC and ETH, and
            the indices that drive global sentiment. All from a single dashboard.
          </p>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/5 border border-white/10">
            <MarketCard
              title="Forex &amp; Metals"
              tag="22+ instruments"
              items={["XAU/USD", "EUR/USD", "GBP/USD", "USD/JPY", "XAG/USD"]}
            />
            <MarketCard
              title="Crypto"
              tag="24/7 trading"
              items={["BTC/USD", "ETH/USD"]}
            />
            <MarketCard
              title="Indices"
              tag="US market hours"
              items={["US30 · Dow", "US100 · Nasdaq", "US500 · S&P"]}
            />
          </div>
        </div>
      </section>

      {/* Feature grid */}
      <section className="border-b border-white/5">
        <div className="max-w-6xl mx-auto px-5 py-20 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/5 border border-white/5">
          <Feature
            icon={<Zap className="w-5 h-5 text-teal-400" strokeWidth={1.5} />}
            title="Always on"
            body="Cloud execution polls your broker every few seconds. Orders fire in under 2 seconds — no laptop, no VPN, no worries."
          />
          <Feature
            icon={<Shield className="w-5 h-5 text-teal-400" strokeWidth={1.5} />}
            title="Your risk, your rules"
            body="Set lot size, daily caps, hard stop-loss, take-profit, and auto break-even. The engine respects every limit you define."
          />
          <Feature
            icon={<Lock className="w-5 h-5 text-teal-400" strokeWidth={1.5} />}
            title="Encrypted vault"
            body="Broker credentials encrypted at rest and never exposed to the browser. You stay in control of your funds — we never hold them."
          />
        </div>
      </section>

      {/* How it works */}
      <section className="border-b border-white/5 bg-[#0a0a0c]">
        <div className="max-w-6xl mx-auto px-5 py-20 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-teal-400 mb-4 font-mono">
              // Three steps
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight leading-tight">
              Live in <span className="text-teal-400">under 60 seconds</span>.
            </h2>
            <div className="mt-8 space-y-5">
              <Step n="1" title="Connect your broker"
                    body="Add your MT5 credentials once. Encrypted, stored server-side, and used only to execute on your behalf." />
              <Step n="2" title="Set your guard-rails"
                    body="Lot size, max drawdown, take-profit, break-even — the limits the bot will never cross." />
              <Step n="3" title="Press Start"
                    body="The engine takes over. Watch live PnL, get Telegram alerts on wins, and review every trade on the dashboard." />
            </div>
          </div>

          {/* Live stats mock */}
          <div className="relative">
            <div className="absolute -inset-px bg-gradient-to-br from-teal-400/30 via-transparent to-transparent rounded-sm" />
            <div className="relative border border-white/10 bg-[#0c0c0e] rounded-sm p-6 font-mono text-xs space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Activity className="w-4 h-4 text-teal-400 animate-pulse" strokeWidth={1.5} />
                  <span className="text-teal-400 uppercase tracking-widest text-[10px]">Live · last 24h</span>
                </div>
                <span className="text-zinc-600 text-[10px]">demo data</span>
              </div>
              <div className="border-t border-white/5" />
              <Row label="Trades executed" value="38" />
              <Row label="Win rate" value="62.5%" tone="teal" />
              <Row label="Avg trade duration" value="00:01:23" />
              <Row label="Best mover" value="XAU/USD" tone="teal" />
              <Row label="Floating P&amp;L" value="+$182.40" tone="teal" />
              <div className="border-t border-white/5" />
              <div className="text-zinc-600 text-[10px]">// engine v4 · region: us-east · ping 41ms</div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="border-b border-white/5">
        <div className="max-w-6xl mx-auto px-5 py-14 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          <TrustItem icon={<Globe className="w-5 h-5" strokeWidth={1.5} />} label="Multi-region cloud" />
          <TrustItem icon={<Shield className="w-5 h-5" strokeWidth={1.5} />} label="Bank-grade encryption" />
          <TrustItem icon={<TrendingUp className="w-5 h-5" strokeWidth={1.5} />} label="Auto break-even" />
          <TrustItem icon={<Zap className="w-5 h-5" strokeWidth={1.5} />} label="Telegram alerts" />
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-teal-400/[0.04] to-transparent pointer-events-none" />
        <div className="max-w-3xl mx-auto px-5 py-24 text-center relative">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight">
            Stop watching charts.<br />
            <span className="bg-gradient-to-r from-teal-300 to-cyan-400 bg-clip-text text-transparent">
              Start collecting results.
            </span>
          </h2>
          <p className="text-zinc-400 mt-5 leading-relaxed">
            Spin up an account in under a minute. Connect your broker. The bot does
            the rest.
          </p>
          <Link to="/register" data-testid="footer-cta-register"
                className="group inline-flex items-center gap-2 mt-9 bg-teal-400 text-[#09090B] hover:bg-teal-300 px-8 py-4 text-sm font-mono uppercase tracking-widest rounded-sm font-medium transition-all">
            Create your account
            <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" strokeWidth={2} />
          </Link>
          <div className="mt-5 text-[10px] text-zinc-600 font-mono">
            No credit card · Cancel anytime · BYO broker
          </div>
        </div>
      </section>

      <footer className="border-t border-white/5">
        <div className="max-w-6xl mx-auto px-5 py-8 flex flex-col sm:flex-row justify-between items-center gap-3 text-[11px] text-zinc-600 font-mono">
          <div>© {new Date().getFullYear()} BonBot · All rights reserved</div>
          <div className="flex items-center gap-4">
            <a href="mailto:support@bonbot.biz" className="hover:text-zinc-300 transition-colors">support@bonbot.biz</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Feature({ icon, title, body }) {
  return (
    <div className="bg-[#0b0b0d] p-7 hover:bg-[#0d0d0f] transition-colors">
      <div className="w-10 h-10 rounded-sm bg-teal-400/10 border border-teal-400/20 flex items-center justify-center mb-4">{icon}</div>
      <div className="text-sm font-medium tracking-wide text-zinc-100">{title}</div>
      <p className="text-zinc-500 mt-2 text-sm leading-relaxed">{body}</p>
    </div>
  );
}

function MarketCard({ title, tag, items }) {
  return (
    <div className="bg-[#0b0b0d] p-7 hover:bg-[#0d0d0f] transition-colors">
      <div className="flex items-baseline justify-between mb-4">
        <div className="text-sm font-medium tracking-wide text-zinc-100" dangerouslySetInnerHTML={{ __html: title }} />
        <span className="text-[10px] font-mono uppercase tracking-widest text-teal-400">{tag}</span>
      </div>
      <ul className="space-y-1.5">
        {items.map((it) => (
          <li key={it} className="text-xs text-zinc-400 font-mono flex items-center gap-2">
            <span className="text-teal-400/60">·</span>
            <span dangerouslySetInnerHTML={{ __html: it }} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function Step({ n, title, body }) {
  return (
    <div className="flex gap-4">
      <div className="shrink-0 w-8 h-8 rounded-sm border border-teal-400/30 bg-teal-400/[0.05] flex items-center justify-center text-teal-400 text-sm font-mono">
        {n}
      </div>
      <div>
        <div className="text-sm font-medium text-zinc-100">{title}</div>
        <p className="text-xs text-zinc-500 leading-relaxed mt-1">{body}</p>
      </div>
    </div>
  );
}

function TrustItem({ icon, label }) {
  return (
    <div className="flex flex-col items-center gap-2 text-zinc-500">
      <div className="text-teal-400/70">{icon}</div>
      <div className="text-[11px] font-mono uppercase tracking-widest">{label}</div>
    </div>
  );
}

function Stat({ value, label, tone }) {
  return (
    <div className="bg-[#0b0b0d] px-4 py-5 sm:px-6 sm:py-6 hover:bg-[#0d0d0f] transition-colors">
      <div className={`text-2xl sm:text-3xl font-medium tracking-tight ${tone === "teal" ? "text-teal-400" : "text-zinc-100"}`}>{value}</div>
      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-1">{label}</div>
    </div>
  );
}

function Row({ label, value, tone }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-zinc-500">{label}</span>
      <span className={tone === "teal" ? "text-teal-400" : "text-zinc-200"}>{value}</span>
    </div>
  );
}
